import PropTypes from 'prop-types'

import {
  Box,
  Container,
  Icon,
  Heading,
  MonoButton,
  Text,
  Image,
  Tooltip,
  LevelBars,
  TransparentButton,
} from '@redriverpress/system'

import {
  COURSE_TYPE_ICONS,
  COURSE_TYPE_LABELS,
} from 'Common/constants/contentTypes'
import { SELF_STUDY_PRODUCT_TITLE } from 'Common/constants/brand'
import IconLabel from './IconLabel'

const MAX_IMAGE_WIDTH = ['168px', null, '192px', '343px']

const Label = ({ children, ...props }) => (
  <Box
    bg='rgba(255, 255, 255, 0.5)'
    px={2}
    py={1}
    borderRadius='small'
    fontSize='12px'
    lineHeight='19px'
    height={28}
    width='fit-content'
    {...props}
  >
    {children}
  </Box>
)

Label.propTypes = {
  children: PropTypes.node.isRequired,
}

export default function CourseHeader({
  audiences,
  bannerBackground,
  headerImageUrl,
  levels,
  onAddCourseToClass,
  onSaveCourseToFolder,
  subtitle,
  title,
  courseType,
  description,
  displaySelfStudyLabel,
  selfStudyLevelsList,
  userLoggedIn,
}) {
  return (
    <Box bg={bannerBackground} pb={4}>
      <Container display='flex'>
        <Box flex={1} display='flex' flexDirection='column'>
          {/* Info Block */}
          <Box mt={[3, null, 5]}>
            {/* HEADER IMAGE - only for $default and $sm */}
            <Box display={['block', null, 'none']} my={3} textAlign='center'>
              <Image
                width={MAX_IMAGE_WIDTH}
                alt='banner image'
                src={headerImageUrl}
              />
            </Box>

            <Box display={['column', null, 'flex']} alignItems='center'>
              <Label fontWeight='600'>
                <Icon icon={COURSE_TYPE_ICONS[courseType]} pr={2} />
                {COURSE_TYPE_LABELS[courseType]}
              </Label>

              {displaySelfStudyLabel && (
                <Tooltip.Root delayDuration={0}>
                  <Tooltip.Trigger asChild>
                    <Box>
                      <Label ml={[0, null, 2]} mt={[2, null, 0]}>
                        <Box
                          display='flex'
                          alignItems='center'
                          fontWeight='600'
                        >
                          <Icon icon='user-graduate' pr={2} />
                          Available for {SELF_STUDY_PRODUCT_TITLE}
                          {selfStudyLevelsList.length > 0 && (
                            <LevelBars
                              activeColor='black'
                              level={selfStudyLevelsList[0]}
                              outline
                              mx={2}
                            />
                          )}
                          <Text as='span'>Level {selfStudyLevelsList[0]}</Text>
                        </Box>
                      </Label>
                    </Box>
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <Tooltip.Arrow />
                    <Text maxWidth='30ch'>
                      Your students can choose and complete Courses and Study
                      Packs on their own time in the{' '}
                      <strong>{SELF_STUDY_PRODUCT_TITLE}</strong> area of
                      their&nbsp;account.
                    </Text>
                  </Tooltip.Content>
                </Tooltip.Root>
              )}
            </Box>
            {/* Title / sub-title */}
            <Heading fontSize={[4, null, 5]} as='h1' mt={2} mb={1}>
              {title}
            </Heading>
            <Text fontWeight='600' mb={3}>
              {subtitle}
            </Text>

            {/* Description */}
            {description && (
              <Text mt={0} mb={3} measure>
                {description}
              </Text>
            )}

            {/* Audience */}
            <Box
              display='flex'
              mt={[null, null, 2]}
              flexDirection={['column', null, 'row']}
            >
              <IconLabel mr={3} icon='user-graduate' copy={audiences} />
              <IconLabel icon='lightbulb' copy={levels} />
            </Box>

            <Box mt={3} display='flex' alignItems='center'>
              <MonoButton
                onClick={onAddCourseToClass}
                reverse
                width='fit-content'
                small
                adaptive
              >
                <Icon icon='plus' pr={2} />
                Add to class
              </MonoButton>

              {/* Save to folder Button */}
              {userLoggedIn && (
                <TransparentButton
                  ml={2}
                  bg='white'
                  size='2.25rem'
                  borderRadius='small'
                  title='Save course to folder'
                  onClick={onSaveCourseToFolder}
                >
                  <Icon icon='folder-open' />
                </TransparentButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* RIGHT SIDE - HEADER IMAGE */}
        <Box
          py={5}
          justifyContent='center'
          display={['none', null, 'flex']}
          alignItems='center'
          flex={1}
        >
          <Image
            maxWidth={MAX_IMAGE_WIDTH}
            maxHeight='240px'
            alt='banner image'
            src={headerImageUrl}
          />
        </Box>
      </Container>
    </Box>
  )
}

CourseHeader.defaultProps = {
  audiences: null,
  levels: null,
  subtitle: null,
}

CourseHeader.propTypes = {
  audiences: PropTypes.string,
  bannerBackground: PropTypes.string.isRequired,
  headerImageUrl: PropTypes.string.isRequired,
  levels: PropTypes.string,
  onAddCourseToClass: PropTypes.func.isRequired,
  onSaveCourseToFolder: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  courseType: PropTypes.string.isRequired,
  displaySelfStudyLabel: PropTypes.bool.isRequired,
  selfStudyLevelsList: PropTypes.arrayOf(PropTypes.string).isRequired,
  userLoggedIn: PropTypes.bool.isRequired,
}
